// lib/variables.ts
type Notification = { message: string; type?: 'success' | 'error' | 'info' };

class NotificationStore extends EventTarget {
    notify(notification: Notification) {
        this.dispatchEvent(new CustomEvent('notify', { detail: notification }));
    }

    on(event: string, handler: EventListenerOrEventListenerObject) {
        this.addEventListener(event, handler as EventListener);
    }

    off(event: string, handler: EventListenerOrEventListenerObject) {
        this.removeEventListener(event, handler as EventListener);
    }
}

export const notificationStore = new NotificationStore();

/** Asks the services list to expand one entry (by its id in services.json), e.g. from the announcement. */
class ServiceStore extends EventTarget {
    open(id: string) {
        this.dispatchEvent(new CustomEvent('open', { detail: id }));
    }

    on(handler: (id: string) => void) {
        const listener = (e: Event) => handler((e as CustomEvent<string>).detail);
        this.addEventListener('open', listener);
        return () => this.removeEventListener('open', listener);
    }
}

export const serviceStore = new ServiceStore();

/** What the contact form should start with, set by "ask about this" buttons before they scroll to the form. */
export type ContactPrefill = { subject: string; message: string };

class ContactStore extends EventTarget {
    private pending: ContactPrefill | null = null;

    /** Remember a prefill and tell a mounted form; a form that mounts later takes it with `take()`. */
    prefill(p: ContactPrefill) {
        this.pending = p;
        this.dispatchEvent(new CustomEvent('prefill', { detail: p }));
    }

    /** The pending prefill, once. */
    take(): ContactPrefill | null {
        const p = this.pending;
        this.pending = null;
        return p;
    }

    on(handler: (p: ContactPrefill) => void) {
        const listener = (e: Event) => handler((e as CustomEvent<ContactPrefill>).detail);
        this.addEventListener('prefill', listener);
        return () => this.removeEventListener('prefill', listener);
    }
}

export const contactStore = new ContactStore();

/** Lets components deep inside the one-page shell ask it to scroll to a section (ClientShell listens). */
class SectionStore extends EventTarget {
    go(section: string) {
        this.dispatchEvent(new CustomEvent('go', { detail: section }));
    }

    on(handler: (section: string) => void) {
        const listener = (e: Event) => handler((e as CustomEvent<string>).detail);
        this.addEventListener('go', listener);
        return () => this.removeEventListener('go', listener);
    }
}

export const sectionStore = new SectionStore();

/** Opens the NISG announcement on demand (the hero tape), even after it was dismissed. */
class AnnouncementStore extends EventTarget {
    open() {
        this.dispatchEvent(new Event('open'));
    }

    on(handler: () => void) {
        this.addEventListener('open', handler);
        return () => this.removeEventListener('open', handler);
    }
}

export const announcementStore = new AnnouncementStore();
